// src/components/Search.js — full-text search across your channels (25 Oct 2026;
// backend #223). A panel under the channel header: type, see hits with channel
// and snippet, click one to jump to its channel and open the thread it lives in.
// Scope toggles between "this channel" and "all my channels".

import { useState, useEffect, useRef } from 'react';
import { API_BASE as API } from '../utils/apiBase';

const C = { accent: '#14b8a6', text: '#d4d4d8', dim: '#a1a1aa', muted: '#52525b', faint: '#3f3f46', bad: '#f87171' };
const btn = (color = C.accent, on = false) => ({ padding: '6px 12px', borderRadius: 8, border: `1px solid ${color}55`, background: on ? `${color}22` : `${color}0d`, color, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" });

export function SearchPanel({ headers, channel, onJump, onClose }) {
  const [q, setQ] = useState('');
  const [scope, setScope] = useState(channel ? 'channel' : 'all');
  const [hits, setHits] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const term = q.trim();
    if (term.length < 2) { setHits(null); setErr(''); return; }
    timer.current = setTimeout(async () => {
      setBusy(true); setErr('');
      try {
        const p = new URLSearchParams({ q: term, limit: '30' });
        if (scope === 'channel' && channel) p.set('channel', channel.slug);
        const res = await fetch(`${API}/api/signal/search?${p}`, { headers: headers() });
        const d = await res.json().catch(() => ({}));
        if (!res.ok || d.success === false) throw new Error(d.message || `HTTP ${res.status}`);
        setHits(d.data || []);
      } catch (e) { setErr(e.message); setHits([]); }
      setBusy(false);
    }, 300);
    return () => clearTimeout(timer.current);
  }, [q, scope, channel, headers]);

  return (
    <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.04)', maxHeight: '55vh', overflowY: 'auto' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder='Search messages… ("exact phrase", -exclude)'
          aria-label="Search messages"
          style={{ flex: 1, minWidth: 180, padding: '9px 12px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(39,39,42,0.5)', color: '#e4e4e7', fontSize: 13, outline: 'none', fontFamily: "'Inter', sans-serif" }} />
        {channel && <button onClick={() => setScope('channel')} style={btn(C.accent, scope === 'channel')}>#{channel.name}</button>}
        <button onClick={() => setScope('all')} style={btn(C.accent, scope === 'all')}>All my channels</button>
        <button onClick={onClose} style={btn(C.muted)}>Close</button>
      </div>
      {busy && <div style={{ fontSize: 12, color: C.muted }}>Searching…</div>}
      {err && <div style={{ fontSize: 12, color: C.bad }}>{err}</div>}
      {hits && hits.length === 0 && !busy && !err && <div style={{ fontSize: 12, color: C.muted }}>Nothing matches in {scope === 'channel' && channel ? `#${channel.name}` : 'your channels'}.</div>}
      {hits && hits.map((h) => (
        <button key={h._id} onClick={() => onJump(h)}
          style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', marginBottom: 6, borderRadius: 10, border: '1px solid rgba(255,255,255,0.05)', background: 'rgba(24,24,27,0.8)', cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 11, color: C.muted, marginBottom: 3 }}>
            <span style={{ color: C.accent }}>{h.channel?.icon || '💬'} #{h.channel?.name || h.channel?.slug}</span>
            <span>{h.user?.name || 'Unknown'}</span>
            <span>{new Date(h.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}</span>
            {h.inThread && <span style={{ marginLeft: 'auto', color: C.faint }}>in a thread</span>}
          </div>
          <div style={{ fontSize: 13, color: C.text, lineHeight: 1.45, wordBreak: 'break-word' }}>{h.snippet}</div>
        </button>
      ))}
    </div>
  );
}

// ── Daily digest switch (backend #225): one summary a day of your channels, or nothing. ──
export function DigestToggle({ headers }) {
  const [on, setOn] = useState(null); // null = loading
  const [err, setErr] = useState('');
  useEffect(() => {
    fetch(`${API}/api/signal/digest`, { headers: headers() }).then((r) => r.json()).then((d) => setOn(d.data ? d.data.digest !== false : true)).catch(() => setOn(true));
  }, [headers]);
  async function flip() {
    const next = !on; setOn(next); setErr('');
    try {
      const res = await fetch(`${API}/api/signal/digest`, { method: 'PUT', headers: headers(), body: JSON.stringify({ digest: next }) });
      if (!res.ok) throw new Error('Could not save');
    } catch (e) { setOn(!next); setErr(e.message); }
  }
  if (on === null) return null;
  return (
    <button onClick={flip} title={on ? 'Daily digest is on: one summary a day of what moved in your channels. Click to turn off.' : 'Daily digest is off. Click to get one summary a day of what moved in your channels.'}
      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid rgba(96,165,250,0.2)', background: on ? 'rgba(96,165,250,0.14)' : 'rgba(96,165,250,0.06)', color: '#60a5fa', fontSize: 12, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>
      📰 Daily digest {on ? 'on' : 'off'}{err ? ` — ${err}` : ''}
    </button>
  );
}
